import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, SparklesIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

/**
 * CreateAgentChoiceModal
 *
 * 2-step modal:
 *   Step 1 — Choose: "scratch" or "child"
 *   Step 2 — If "child": pick a parent (only agents without parent_agent_id shown)
 *
 * On confirm → navigates to /agents/new or /agents/new?mode=child&parent=<id>
 *
 * When managerChildOnly is true, the "Start from Scratch" (top-level) option is
 * hidden and the modal goes straight to the child-agent parent picker, since
 * managers may only create child agents.
 */
function CreateAgentChoiceModal({ onClose, parentAgents = [], managerChildOnly = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Managers skip the choice step entirely and land on the parent picker.
  const [step, setStep] = useState(managerChildOnly ? 2 : 1);
  const [choice, setChoice] = useState(managerChildOnly ? 'child' : null); // 'scratch' | 'child'
  const [selectedParent, setSelectedParent] = useState('');

  const handleNext = () => {
    if (choice === 'scratch') {
      navigate('/agents/new');
      onClose();
    } else if (choice === 'child') {
      setStep(2);
    }
  };

  const handleCreate = () => {
    if (!selectedParent) return;
    navigate(`/agents/new?mode=child&parent=${selectedParent}`);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'var(--overlay)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-md mx-4 rounded-2xl shadow-xl bg-card border border-border">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="p-6">
          {step === 1 && (
            <>
              <h2 className="text-lg font-semibold text-foreground mb-1">
                {t('agents.choiceModal.title', 'How do you want to create this agent?')}
              </h2>
              <p className="text-sm text-muted mb-6">
                {t('agents.choiceModal.subtitle', 'Start fresh or inherit settings from an existing agent.')}
              </p>

              <div className="space-y-3">
                {/* Scratch option */}
                <button
                  onClick={() => setChoice('scratch')}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${
                    choice === 'scratch'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <SparklesIcon className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">
                        {t('agents.choiceModal.scratchTitle', 'Start from Scratch')}
                      </p>
                      <p className="text-sm text-muted mt-0.5">
                        {t('agents.choiceModal.scratchDesc', 'Build a brand new agent with your own configuration')}
                      </p>
                    </div>
                  </div>
                </button>

                {/* Child option */}
                <button
                  onClick={() => setChoice('child')}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${
                    choice === 'child'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <UserGroupIcon className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">
                        {t('agents.choiceModal.childTitle', 'Create Child Agent')}
                      </p>
                      <p className="text-sm text-muted mt-0.5">
                        {t('agents.choiceModal.childDesc', 'Clone settings from a parent agent and customize')}
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={handleNext}
                  disabled={!choice}
                  className="btn-primary disabled:opacity-40"
                >
                  {t('agents.choiceModal.next', 'Next')}
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              {!managerChildOnly && (
                <button
                  onClick={() => setStep(1)}
                  className="text-sm text-muted hover:text-foreground mb-4 inline-flex items-center gap-1"
                >
                  ← {t('agents.choiceModal.back', 'Back')}
                </button>
              )}

              <h2 className="text-lg font-semibold text-foreground mb-1">
                {t('agents.choiceModal.childTitle', 'Create Child Agent')}
              </h2>
              <p className="text-sm text-muted mb-4">
                {t('agents.choiceModal.selectParent', 'Select a parent agent to inherit settings from')}
              </p>

              <select
                className="input w-full"
                value={selectedParent}
                onChange={(e) => setSelectedParent(e.target.value)}
              >
                <option value="">-- {t('agentEditor.selectParentAgent', 'Select an agent...')} --</option>
                {parentAgents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>

              {parentAgents.length === 0 && (
                <p className="text-xs text-muted mt-2 italic">
                  {t('agents.choiceModal.noParentsAvailable', 'No top-level agents available to use as parent.')}
                </p>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={handleCreate}
                  disabled={!selectedParent}
                  className="btn-primary disabled:opacity-40"
                >
                  {t('agents.choiceModal.create', 'Create')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateAgentChoiceModal;
